import React, { useState, useRef, useEffect } from 'react';
import { validateAndProcessFile } from './DragDropFileUpload';
import './GlobalDragDrop.css';

/**
 * GlobalDragDrop component that adds drag and drop functionality to any page
 * Reuses the existing upload functionality from DragDropFileUpload
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onFileSelect - Function to call when a file is selected
 * @returns {JSX.Element} GlobalDragDrop component
 */
const GlobalDragDrop = ({ onFileSelect }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const dragCounter = useRef(0);
  
  // Set up event listeners for drag and drop
  useEffect(() => {
    const handleDragOver = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };
    
    const handleDragEnter = (event) => {
      event.preventDefault();
      event.stopPropagation();
      dragCounter.current++;
      setIsDragging(true);
    };
    
    const handleDragLeave = (event) => {
      event.preventDefault();
      event.stopPropagation();
      
      dragCounter.current--;
      
      // Only set isDragging to false when all drag events are complete
      // This fixes issues with child elements triggering false dragLeave events
      if (dragCounter.current === 0) {
        setIsDragging(false);
      }
    };
    
    const handleDrop = (event) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);
      dragCounter.current = 0;
      
      if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
        const file = event.dataTransfer.files[0];
        validateAndProcessFile(file, onFileSelect, clearFileInput);
      }
    };
    
    const handleKeyDown = (event) => {
      // If Escape key is pressed while dragging, cancel the operation
      if (event.key === 'Escape' && isDragging) {
        setIsDragging(false);
        dragCounter.current = 0;
      }
    };
    
    // Add global event listeners
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('dragenter', handleDragEnter);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('drop', handleDrop);
    document.addEventListener('keydown', handleKeyDown);
    
    // Remove event listeners on cleanup
    return () => {
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('dragenter', handleDragEnter);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('drop', handleDrop);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onFileSelect, isDragging]);
  
  // Helper function to clear the file input
  const clearFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // Only render the overlay when dragging
  if (!isDragging) {
    return null;
  }
  
  return (
    <div className="global-drag-drop-overlay">
      <div className="global-drag-drop-content">
        <div className="upload-icon">
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 16V8M12 8L8 12M12 8L16 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M3 15V16C3 18.2091 4.79086 20 7 20H17C19.2091 20 21 18.2091 21 16V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <h2>Drop to Upload New Floorplan</h2>
        <p>Release to upload and create a new project</p>
        <p className="cancel-hint">Press ESC to cancel</p>
      </div>
      
      <input
        type="file"
        accept="image/png, image/jpeg, image/jpg, application/pdf"
        ref={fileInputRef}
        className="file-input"
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default GlobalDragDrop; 